import React, { useState, useContext } from 'react';
import { AuthContext } from '../../App';
import { UserIcon } from '@heroicons/react/24/outline';

interface ProfilePictureProps {
  user?: any;
  guest?: any;
  participant?: any;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  name?: string;
}

/**
 * Universelle ProfilePicture-Komponente
 * Zeigt Profilbild mit Fallback zu Icon an
 *
 * @param user - User-Objekt (für EventModal)
 * @param guest - Guest-Objekt (für AddGuestModal)
 * @param participant - Participant-Objekt (für ParticipantsModal)
 * @param size - Größe: 'sm' (32px), 'md' (40px), 'lg' (48px), 'xl' (64px)
 * @param name - Optional: Name override
 */
const ProfilePicture: React.FC<ProfilePictureProps> = ({
  user,
  guest,
  participant,
  size = 'md',
  name
}) => {
  const { config } = useContext(AuthContext);
  const [imageError, setImageError] = useState(false);

  const BASE_URL = process.env.REACT_APP_API_URL || config?.apiUrl || 'http://localhost:4000';

  const sizeClasses = {
    sm: 'h-8 w-8 text-xs',
    md: 'h-10 w-10 text-sm',
    lg: 'h-12 w-12 text-base',
    xl: 'h-16 w-16 text-lg'
  };

  // Determine user ID and display name from different sources
  let userId: string | number | undefined;
  let displayName: string;

  if (user) {
    userId = user.id || user.mg_nr;
    displayName = name || user.name || user.vorname || 'Unknown';
  } else if (guest) {
    userId = guest.mg_nr;
    displayName = name || `${guest.vorname || ''} ${guest.name || ''}`.trim() || 'Unknown';
  } else if (participant) {
    userId = participant.mg_nr || participant.member_id;
    displayName = name || `${participant.vorname || ''} ${participant.nachname || participant.name || ''}`.trim() || 'Unknown';
  } else {
    userId = undefined;
    displayName = name || 'Unknown';
  }

  // Try to load image if userId exists
  if (!imageError && userId) {
    return (
      <img
        src={`${BASE_URL}/api/members/user-image-url/${userId}?size=S&token=${localStorage.getItem('jwt_token')}`}
        alt={displayName}
        className={`${sizeClasses[size]} rounded-full object-cover border-2 border-white dark:border-gray-700 shadow-sm`}
        onError={() => setImageError(true)}
      />
    );
  }

  // Fallback to gradient icon
  return (
    <div className={`${sizeClasses[size]} rounded-full bg-primary-600 flex items-center justify-center shadow-sm border-2 border-white dark:border-gray-700`}>
      <UserIcon className="h-3/5 w-3/5 text-white/70" />
    </div>
  );
};

export default ProfilePicture;
